import { connect, useDispatch } from 'react-redux';
import { Grid, Button, CircularProgress, Box, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';
import get from 'lodash/get';
import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import CardWithLabel from '../../components/CardWithLabel';
import { ontologyMasterActions } from '../../store/reducers/ontology-master';
import { OntologyLevels } from '../../store/models/ontoloy-master';
import PopUp from '../../components/Popup';
import { ReactComponent as SuccessIcon } from '../../assets/icons/main/successIcon.svg';
import { ReactComponent as FailedIcon } from '../../assets/icons/main/failedIcon.svg';
import useNotification from '../../common/notification';
import { ontologySchema } from './form';
import { isEmpty } from 'lodash';
import { ReactComponent as SaveOutlinedIcon } from '../../assets/icons/main/save-icon.svg';
import {palette} from '../../theme/palette';
import { useOutletContext } from 'react-router-dom';

const Styles = {
  button: {
    padding: '9px 16px 9px 16px',
    borderRadius: '8px',
    marginRight: '48px',
    width: 'auto',
     },
  box: {
    height: '72px',
    alignItems: 'center',
    position: 'fixed',
    zIndex: 999,
    top: '99px',
    width: '100%',
    left: 0,
    display: 'flex',
    justifyContent: 'end',
    '@media (max-width:1024px)':{
      width: '100%',
    }
  },
};

interface OntologyLevelTypes {
  isOntologyDataLoading: boolean;
  isSaveOntologySuccess: boolean;
  isSaveOntologyLoading: boolean;
  ontologyData: any;
  isSaveOntologyFailed: boolean;
  ontologyMessage: string;
  ontologyDataHasError: boolean;
  userAccess: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    approver: boolean;
  };
}

const OntologyLevelMaster = ({
  isOntologyDataLoading,
  ontologyData,
  isSaveOntologySuccess,
  isSaveOntologyFailed,
  ontologyMessage,
  ontologyDataHasError,
  userAccess,
}: OntologyLevelTypes) => {
  const themeData: any = get(useOutletContext(), 'themeData', {});
  const dispatch = useDispatch();
  const responsiveSize =  useMediaQuery('(min-width: 1536px)');
  const ajv = new Ajv({ allErrors: true });
  AjvErrors(ajv);
  const [msg, sendNotification] = useNotification();
  const [levelData, setLevelData] = useState<Array<OntologyLevels>>([]);
  const [successPopup, setSuccessPopup] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [ajvErrors, setAjvErrors] = useState<any>({});
  const [isValidLevelName, setIsValidLevelName] = useState<any>({});

  useEffect(() => {
    dispatch(ontologyMasterActions.getOntologyList(''));
  }, []);

  useEffect(() => {
    if (isSaveOntologySuccess) {
      sendNotification({
        msg: ontologyMessage,
        variant: 'success',
        dispatchAction: ontologyMasterActions.resetLoadingState(),
      });
      dispatch(ontologyMasterActions.getOntologyList(''));
    }

    if (isSaveOntologyFailed || ontologyDataHasError) {
      sendNotification({
        msg: ontologyMessage,
        variant: 'error',
        dispatchAction: ontologyMasterActions.resetLoadingState(),
      });
    }
  }, [isSaveOntologySuccess, isSaveOntologyFailed, ontologyDataHasError]);

  useEffect(() => {
    if (!ontologyData.length) {
      setLevelData([{ level_number: 1, level_name: '', isSaved: false }]);
    } else {
      const sortedOntologyData = ontologyData
        .map((item: any) => {
          return { ...item, isSaved: true };
        })
        .sort((a: any, b: any) => a.level_number - b.level_number);
      setLevelData(sortedOntologyData);
    }
  }, [ontologyData]);

  const onAddCard = () => {
    setLevelData((prevData: any) => [
      ...prevData,
      { level_number: prevData.length + 1, level_name: '', isSaved: false },
    ]);
  };

  const onDeleteCard = (levelNumber: number) => {
    setLevelData((prevData: any) =>
      prevData
        .filter((card: any) => card.level_number !== levelNumber)
        .map((item: any, index: any) => {
          return { ...item, level_number: index + 1 };
        })
    );
  };

  const onEditClick = (levelNumber: number) => {
    setLevelData((prevData: any) =>
      prevData.map((item: any) => {
        if (item.level_number == levelNumber) {
          return { ...item, editable: true };
        } else {
          return { ...item };
        }
      })
    );
  };

  const onEditCancel = (levelNumber: number) => {
    setLevelData((prevData: any) =>
      prevData.map((item: any) => {
        if (item.level_number == levelNumber) {
          return { ...item, editable: false };
        } else {
          return { ...item };
        }
      })
    );
  };

  const saveOntologyLevels = () => {
    setIsValidLevelName({});
    // const requestData = levelData.filter((item: any) => !item.isSaved);

    const levelSchema = {
      ...ontologySchema,
      items: [...levelData.map((item: any) => ({ ...ontologySchema.items[0] }))],
    };
    const validate = ajv.compile(levelSchema);

    const trimmedLeveldata = levelData.map((item: any) => {
      return { ...item, level_name: typeof item.level_name === 'string' ? item.level_name.trim() : item.level_name };
    });
    setLevelData(trimmedLeveldata);

    if (validate([...trimmedLeveldata])) {
      if (isEmpty(ontologyData)) {
        dispatch(ontologyMasterActions.saveOntologyData(trimmedLeveldata));
      } else {
        dispatch(ontologyMasterActions.updateLevelmasterBulk(trimmedLeveldata));
      }

      setAjvErrors({});
    } else {
      if (validate.errors) {
        setAjvErrors(validate.errors[0]);
      }
    }
  };

  const enterOntologyLevelName = (e: any, index: number) => {
    const { name, value } = e.target;
    const updatedSchema = levelData.map((schema: any) => {
      if (schema.level_number === index) {
        return {
          ...schema,
          [name]: value,
        };
      }
      return schema;
    });
    setLevelData(updatedSchema);

    if (levelData.findIndex((e: any) => e.level_name.toLowerCase() === value.toLowerCase()) > -1) {
      var obj = {
        [index]: true,
      };
      setIsValidLevelName(obj);
    } else {
      setIsValidLevelName({});
    }
  };

  const closeAlertModal = () => {
    dispatch(ontologyMasterActions.resetLoadingState());
    setSuccessPopup(false);
    setErrorMsg('');
  };

  const renderAlertModal = () => (
    <PopUp
      title={isSaveOntologySuccess ? 'Success' : 'Failed'}
      open={successPopup}
      icon={isSaveOntologySuccess ? <SuccessIcon  style={{ color: palette.success.dark }} /> : <FailedIcon style={{color : palette.error.main }} />}
      message={errorMsg}
      confirmation
      confirmLabel='Ok'
      onConfirm={closeAlertModal}
      onClose={closeAlertModal}
    />
  );

  if (isOntologyDataLoading) {
    return <CircularProgress />;
  }
  return (
    <Box sx={{ position: 'relative', minHeight: 'calc(100vh - 174px)' , paddingRight: responsiveSize ? '8px' : 0 }}>
      <Box sx={{...Styles.box, backgroundColor: themeData.palette.background.default}}>
        <Button
          startIcon={<SaveOutlinedIcon />}
          variant='contained'
          size='medium'
          sx={Styles.button}
          onClick={saveOntologyLevels}
          disabled={!userAccess.create && !userAccess.update}
        >
          Save
        </Button>
      </Box>
      <Grid sx={{ paddingTop: '30px' }} container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3, lg: 3 }}>
        {levelData.map((items: any, index: any) => (
          <Grid item key={items.level_number} xs={12} sm={12} md={6} lg={4} xl={3}>
            <CardWithLabel
              number={items.level_number}
              onAddCard={onAddCard}
              onDeleteCard={onDeleteCard}
              addCard={false}
              colorval={index}
              inputValue={items}
              onInputChange={enterOntologyLevelName}
              isSaved={items.isSaved}
              setAjvErrors={setAjvErrors}
              onEditClick={onEditClick}
              onEditCancel={onEditCancel}
              editable={get(items, 'editable', false)}
              levelData={levelData}
              isError={
                isValidLevelName[index + 1] || get(ajvErrors, 'instancePath', '').includes(`/${index}/level_name`)
              }
              errorMsg={
                isValidLevelName[index + 1]
                  ? 'Level name already present'
                  : get(ajvErrors, 'instancePath', '').includes(`/${index}/level_name`) && get(ajvErrors, 'message', '')
              }
            />
          </Grid>
        ))}
        <Grid item xs={12} sm={12} key={0} md={6} lg={4} xl={3}>
          <CardWithLabel number='1234' onAddCard={onAddCard} addCard={true} />
        </Grid>
      </Grid>
      {renderAlertModal()}
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  ontologyData: state.ontologyMaster.ontologyData,
  isOntologyDataLoading: state.ontologyMaster.isOntologyDataLoading,
  isSaveOntologyLoading: state.ontologyMaster.isSaveOntologySuccess,
  isSaveOntologySuccess: state.ontologyMaster.isSaveOntologySuccess,
  isSaveOntologyFailed: state.ontologyMaster.isSaveOntologyFailed,
  ontologyMessage: state.ontologyMaster.ontologyMessage,
  ontologyDataHasError: state.ontologyMaster.ontologyDataHasError,
  userAccess: get(state, `profile.userAccess.site/levels`, {}),
});

export default connect(mapStateToProps)(OntologyLevelMaster);
